// `clientSideFavorCandidate` / `clientSideHideCandidate` / `clientSideUnhideCandidate` —
// what the row looks like immediately after the write, before any refetch.
//
// ⚠️ THE LOCAL UPDATE IS NOT COSMETIC AND IT IS NOT A GUESS. Upstream applies these flags
// itself rather than refetching, and the combinations are the point: favouring a candidate
// also UNARCHIVES them, and archiving one also clears the favourite AND marks them viewed.
// A port that flips only the flag the button names leaves a row that is both favourite and
// archived — a state the server will never return, and which both filters then disagree about.
import type { AlertCandidate } from '@woo-ext/api'

/** The subset these writes touch. */
export type CandidateFlags = Pick<AlertCandidate, 'favorite' | 'hidden' | 'hiddenDate' | 'viewed'>

/** `clientSideFavorCandidate` — favourite on, archive off. */
export function applyFavor<T extends CandidateFlags>(candidate: T): T {
  return { ...candidate, favorite: true, hidden: false, hiddenDate: undefined }
}

/**
 * `clientSideHideCandidate` — archived, with the server's date, and NOT a favourite any more.
 *
 * ⚠️ `viewed: true` IS PART OF IT. Archiving something counts as having seen it, so an
 * archived candidate never reappears under the "Unviewed" filter. Leaving it out is invisible
 * until a recruiter archives from the Unviewed list and the row stays.
 */
export function applyHide<T extends CandidateFlags>(candidate: T, hiddenDate: number): T {
  return { ...candidate, hidden: true, hiddenDate, favorite: false, viewed: true }
}

/**
 * The inverse of `applyFavor`.
 *
 * ⚠️ IT DOES NOT TOUCH THE ARCHIVE FLAGS, where `applyFavor` clears them. Favouring an
 * archived candidate un-archives it — that is upstream's rule — but un-favouring one is not
 * the same event in reverse: it leaves archive alone, because a candidate that was never
 * archived must not become archived by un-starring it.
 */
export function applyUnfavor<T extends CandidateFlags>(candidate: T): T {
  return { ...candidate, favorite: false }
}

/** `clientSideUnhideCandidate` — the archive flags cleared, the favourite NOT restored. */
export function applyUnhide<T extends CandidateFlags>(candidate: T): T {
  return { ...candidate, hidden: false, hiddenDate: undefined }
}

/** Apply one of the above across a list, by id. */
export function applyToIds<T extends CandidateFlags & { id: number }>(
  candidates: readonly T[],
  ids: ReadonlySet<number>,
  apply: (candidate: T) => T,
): T[] {
  return candidates.map((candidate) => (ids.has(candidate.id) ? apply(candidate) : candidate))
}
